'use client'

import { AuthorizationContext, AuthorizationState } from '@entities/authentication'
import { DeferredLoginTracker } from '@features/auth/ui/deferred-login-tracker'
import { getBrowserAuthSession, isNewerAccessToken } from '@shared/libs/browser-auth-session'
import { MetaContextObserver } from '@shared/marketing/meta-context-observer'
import { ReactNode, Suspense, useEffect, useMemo, useRef, useSyncExternalStore } from 'react'

import { KakaoInitStore } from '../model'

const subscribeToSession = (listener: () => void) => getBrowserAuthSession()?.subscribe(listener) ?? (() => {})
const getRecoveredSession = () => getBrowserAuthSession()?.getSnapshot() ?? null
const getServerSession = () => null

export const AuthenticationProvider = ({
  children,
  authorizationState,
}: {
  children: ReactNode
  authorizationState: AuthorizationState | null
}) => {
  const recoveredSession = useSyncExternalStore(subscribeToSession, getRecoveredSession, getServerSession)
  const currentAuthorization = useMemo(
    () =>
      authorizationState?.isAuthorized &&
      recoveredSession?.userId === String(authorizationState.uid) &&
      !isNewerAccessToken(authorizationState.accessToken, recoveredSession.accessToken)
        ? {
            ...authorizationState,
            bearerToken: recoveredSession.accessToken,
            accessToken: recoveredSession.accessToken,
            refreshToken: recoveredSession.refreshToken,
            token: recoveredSession.userToken,
          }
        : authorizationState,
    [authorizationState, recoveredSession],
  )

  useEffect(() => {
    if (authorizationState?.isAuthorized) {
      getBrowserAuthSession()?.setIdentity(String(authorizationState.uid), authorizationState.accessToken)
    }
  }, [authorizationState])

  const kakaoInitStoreRef = useRef(new KakaoInitStore())
  const isKakaoInitialized = useSyncExternalStore(
    kakaoInitStoreRef.current.subscribe,
    kakaoInitStoreRef.current.getSnapshot,
    kakaoInitStoreRef.current.getServerSnapshot,
  )

  useEffect(() => {
    if (
      !currentAuthorization ||
      !currentAuthorization.isAuthorized ||
      currentAuthorization.oauthProviderName !== 'kakao' ||
      !currentAuthorization.oauthProviderAccessToken ||
      !isKakaoInitialized
    ) {
      return
    }

    window.Kakao?.Auth.setAccessToken(currentAuthorization.oauthProviderAccessToken)
  }, [currentAuthorization, isKakaoInitialized])

  return (
    <AuthorizationContext.Provider value={currentAuthorization}>
      {/* 구버전 앱 웹뷰의 ?dlcode= deferred deeplink 자동 로그인은 서버 미들웨어(withDeferredLogin)가
          보호 리다이렉트보다 먼저 단일 처리한다. 클라이언트까지 dlcode 가 도달하지 않는다.
          교환 결과(?dl_result=)만 클라로 넘어오며, DeferredLoginTracker 가 1회 적재 후 제거한다. */}
      <DeferredLoginTracker />
      <Suspense fallback={null}>
        <MetaContextObserver
          bearerToken={currentAuthorization?.isAuthorized ? currentAuthorization.bearerToken : undefined}
        />
      </Suspense>
      {children}
    </AuthorizationContext.Provider>
  )
}
